'use client'

import { createContext, type PropsWithChildren, use, useRef } from 'react'
import { useStore } from 'zustand'

import type { KnownSearchParams } from '../../page'
import { type CoinsTableStore, createCoinsTableStore, defaultInitialState } from './store'
import { normalizeSearchParams } from './utils'

export type CoinsTableStoreApi = ReturnType<typeof createCoinsTableStore>

const CoinsTableStoreContext = createContext<CoinsTableStoreApi | undefined>(undefined)

export function CoinsTableProvider({
  children,
  ssrSearchParams,
  isOGPrice,
}: PropsWithChildren<{ ssrSearchParams: KnownSearchParams; isOGPrice?: boolean }>) {
  const storeRef = useRef<CoinsTableStoreApi | null>(null)
  if (storeRef.current === null) {
    const normalizedSearchParams = normalizeSearchParams(ssrSearchParams, isOGPrice)
    storeRef.current = createCoinsTableStore({ ...defaultInitialState, ...normalizedSearchParams })
  }

  return <CoinsTableStoreContext value={storeRef.current}>{children}</CoinsTableStoreContext>
}

export const useCoinsTableStore = <T,>(selector: (store: CoinsTableStore) => T) => {
  const coinsTableStoreContext = use(CoinsTableStoreContext)

  if (!coinsTableStoreContext) {
    throw new Error(`useCoinsTableStore must be used within CoinsTableProvider`)
  }

  return useStore(coinsTableStoreContext, selector)
}
